/**
 * Promise-based deduplicating caches for bulk download API calls.
 *
 * Multiple callers (WorkActions, show_bulk_download_modal) hit the same
 * endpoints for the same work_id on every LISWP page load. These caches
 * ensure only one network request fires per work per endpoint: the first
 * caller triggers the fetch and every subsequent caller receives the same
 * settled promise. Rejected promises are evicted so transient failures
 * don't stick for the session.
 */

import routes from 'sitewide-js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import {
  BulkDownloadFilter,
  BulkDownloadFilterCounts,
} from 'js/bulk_download/bulk_download_helper';
import { BundleWork } from 'js/bulk_download/work_cards_shared/types';

export type BulkDownloadCountsResponse = {
  show_bulk_download: boolean;
  filter_counts: BulkDownloadFilterCounts;
  total_bulk_download_count: number;
  purchased: boolean;
  show_upsells: boolean;
  auto_bulk_download: boolean;
  selected_filters?: BulkDownloadFilter[];
  gate_auto_download: boolean;
  valid_auto_download_source?: string;
  auto_download_books_ri_id?: number;
  auto_download_books_ri_slug?: string;
};

/**
 * Extract `overrides[*]` query params from the current page URL so admin
 * country-code overrides are forwarded to API requests.
 */
export function getOverrideParams(): Record<string, string> {
  const params: Record<string, string> = {};
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.forEach((value, key) => {
    if (key.startsWith('overrides[')) {
      params[key] = value;
    }
  });
  return params;
}

const countsCache = new Map<number, Promise<BulkDownloadCountsResponse>>();

/**
 * Fetch bulk download counts for a work, deduplicating concurrent requests.
 * When no extra params are provided, the promise is cached by workId so
 * subsequent callers (e.g. the sidebar widget) receive the same result.
 * Requests with extra params (auto-download flags from email redirects)
 * bypass the cache to ensure those session-specific fields are correct.
 */
export function fetchBulkDownloadCountsCached(
  workId: number,
  extraParams?: Record<string, string>,
): Promise<BulkDownloadCountsResponse> {
  const hasExtra = extraParams && Object.keys(extraParams).length > 0;

  if (!hasExtra) {
    const existing = countsCache.get(workId);
    if (existing) return existing;
  }

  const promise = fetchWithCsrf(
    routes.api_v0_work_bulk_download_counts_path({
      work_id: workId,
      subdomain_param: 'api',
      ...extraParams,
      ...getOverrideParams(),
    }),
    { method: 'GET' },
  )
    .then((r) => {
      if (!r.ok) throw new Error('Unable to fetch bulk download counts');
      return r.json() as Promise<BulkDownloadCountsResponse>;
    })
    .catch((err) => {
      if (!hasExtra) countsCache.delete(workId);
      throw err;
    });

  if (!hasExtra) {
    countsCache.set(workId, promise);
  }

  return promise;
}

type PreviewWorksResponse = { bundleWorks?: BundleWork[] };

const previewCache = new Map<number, Promise<BundleWork[]>>();

/**
 * Fetch preview works for a work, deduplicating concurrent requests.
 * Multiple callers may request the same work concurrently; this cache
 * ensures only one network request fires per work.
 */
export function fetchPreviewWorksCached(workId: number): Promise<BundleWork[]> {
  const existing = previewCache.get(workId);
  if (existing) return existing;

  const url = routes.works_api_v0_work_bulk_download_counts_path({
    subdomain_param: 'api',
    work_id: workId,
  });

  const promise = fetchWithCsrf(url, { method: 'GET' })
    .then((r) =>
      r.ok
        ? (r.json() as Promise<PreviewWorksResponse>)
        : Promise.reject(new Error('HTTP ' + r.status)),
    )
    .then((data) => data.bundleWorks ?? [])
    .catch((err) => {
      previewCache.delete(workId);
      throw err;
    });

  previewCache.set(workId, promise);
  return promise;
}
